import { Button, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { type AuditEvent, admin } from "../../data/api";
import { useLoad } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import { Empty, Failure, Mono, PageHeader, Section } from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import type { TenantPageProps } from "../pages";

// ---
// The audit log
//
// Read-only: every mutation writes its entry in the same transaction as the change it records.
// ---

export function Audit({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const tokens = useTokens();
  const events = useLoad(() => admin.audit(tenant, token), [tenant, token]);

  return (
    <>
      <PageHeader
        title="Logs"
        action={
          <Button variant="outlined" onClick={events.reload}>
            Refresh
          </Button>
        }
      />
      <Section flush>
        <Failure error={events.error} />
        {events.data?.length === 0 ? (
          <Empty>Nothing recorded yet.</Empty>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Outcome</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.data?.map((event: AuditEvent) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <StatusBadge
                      label={event.outcome}
                      tone={event.outcome === "denied" ? "danger" : "success"}
                    />
                  </TableCell>
                  <TableCell>
                    <Mono>{event.action}</Mono>
                  </TableCell>
                  <TableCell>
                    <Mono sx={{ fontSize: 11, color: tokens.ink.muted }}>
                      {JSON.stringify(event.target)}
                    </Mono>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap", color: tokens.ink.muted }}>
                    {new Date(event.createdAt).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </>
  );
}
